
import { GoogleGenAI, Type } from "@google/genai";
import { AIEvent } from "../types";
import { CATEGORIES } from "../constants";

// Mandatory: Initialize with process.env.GEMINI_API_KEY directly
const getApiKey = () => {
  const key = process.env.GEMINI_API_KEY;
  if (!key || key === "") {
    console.warn("GEMINI_API_KEY is missing. Discovery features will not work.");
    return "MISSING_KEY";
  }
  return key;
};

export const geminiService = {
  /**
   * Search for upcoming AI events using Grounding Search
   */
  async discoverEvents(keywords: string[]): Promise<Partial<AIEvent>[]> {
    const apiKey = getApiKey();
    if (apiKey === "MISSING_KEY") {
      throw new Error("Gemini API Key is not configured. Please add GEMINI_API_KEY to your environment variables.");
    }

    const ai = new GoogleGenAI({ apiKey });

    try {
      const today = new Date().toISOString().split('T')[0];
      const prompt = `Search for major upcoming global AI and tech events scheduled on or after today, ${today}.
      Focus on: ${keywords.join(', ')}. 
      Extract: Official name, dates, location, focus area, website URL, audience size, and target audience.`;

      const response = await ai.models.generateContent({
        model: 'gemini-3.1-pro-preview', // Updated to recommended model name
        contents: prompt,
        config: {
          tools: [{ googleSearch: {} }],
        },
      });

      // Use .text property directly
      const results = response.text || '';
      return await this.normalizeEvents(results);
    } catch (err: any) {
      console.error("Gemini Discovery Failed:", err);
      if (err.message?.includes('fetch') || err.name === 'TypeError') {
        throw new Error("Network Error: Unable to reach Gemini API. Check your internet connection.");
      }
      throw err;
    }
  },

  /**
   * Normalize unstructured text into the strict Pulse schema
   */
  async normalizeEvents(rawText: string): Promise<Partial<AIEvent>[]> {
    const apiKey = getApiKey();
    if (apiKey === "MISSING_KEY") {
      throw new Error("Gemini API Key is not configured.");
    }

    const ai = new GoogleGenAI({ apiKey });

    try {
      const today = new Date().toISOString().split('T')[0];
      const prompt = `Convert the following event information into a structured JSON array.
      Categories: ${CATEGORIES.join(', ')}.
      
      Data: ${rawText}`;

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview', // Faster model for structured parsing
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                campaignName: { type: Type.STRING },
                category: { type: Type.STRING },
                url: { type: Type.STRING },
                priority: { type: Type.STRING },
                year: { type: Type.NUMBER },
                month: { type: Type.NUMBER },
                startDate: { type: Type.STRING },
                endDate: { type: Type.STRING },
                location: { type: Type.STRING },
                focusArea: { type: Type.STRING },
                information: { type: Type.STRING },
                attendeeSize: { type: Type.STRING },
                targetAudience: { type: Type.STRING },
                addedAt: { type: Type.STRING }
              },
              required: ["campaignName", "category", "startDate"]
            }
          }
        }
      });

      return JSON.parse(response.text || '[]');
    } catch (err: any) {
      console.error("Gemini Normalization Failed:", err);
      if (err.message?.includes('fetch') || err.name === 'TypeError') {
        throw new Error("Network Error: Unable to reach Gemini API during normalization.");
      }
      return [];
    }
  }
};
